import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';

import { useT, type MessageKey } from '@/i18n';
import type { ThemeChoice } from '@/lib/storage';
import { useTheme } from '@/theme/useTheme';

import { MonitorIcon, MoonIcon, SunIcon } from './Icons';
import styles from './Layout.module.css';
import { ThemePreview } from './ThemePreview';

const OPTIONS: readonly { readonly id: ThemeChoice; readonly label: MessageKey; readonly icon: React.JSX.Element }[] = [
  { id: 'auto', label: 'theme.system', icon: <MonitorIcon size={14} /> },
  { id: 'light', label: 'theme.light', icon: <SunIcon size={14} /> },
  { id: 'dark', label: 'theme.dark', icon: <MoonIcon size={14} /> },
];

/** Where the middle of each option sits in the track: 1 px border + 2 px padding, 26 px options 2 px apart. */
const optionCentre = (index: number): number => 3 + index * 28 + 13;

/**
 * Appearance, in the footer: three small icons in one track — system (the
 * default), light, dark — the chosen one tinted. A radio group: Tab reaches
 * the chosen icon, the arrow keys move and choose; each says what it is.
 * Pointing at an option (or reaching it with the keyboard) brings up a small
 * card above the track with a miniature of the Bridge window in that theme.
 */
export function ThemeSwitch(): React.JSX.Element {
  const t = useT();
  const { choice, resolved, set } = useTheme();
  const group = useRef<HTMLDivElement>(null);
  const [peek, setPeek] = useState<number | null>(null);

  useEffect(() => {
    if (peek === null) return undefined;
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') setPeek(null);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [peek]);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const nextIndex = (index + step + OPTIONS.length) % OPTIONS.length;
    const next = OPTIONS[nextIndex];
    if (!next) return;
    set(next.id);
    group.current?.querySelector<HTMLButtonElement>(`[data-theme-option="${next.id}"]`)?.focus();
  };

  const peeked = peek === null ? null : OPTIONS[peek];
  const caption = peeked === undefined || peeked === null ? '' : peeked.id === 'auto' ? t(resolved === 'dark' ? 'themeSwitch.captionDark' : 'themeSwitch.captionLight') : t(peeked.label);

  return (
    <div
      ref={group}
      className={styles.themeSwitch}
      role="radiogroup"
      aria-label={t('settings.appearance')}
      data-testid="theme-switch"
      data-theme-choice={choice}
      onPointerLeave={() => {
        setPeek(null);
      }}
    >
      {OPTIONS.map((option, index) => {
        const name = option.id === 'auto' ? t(resolved === 'dark' ? 'themeSwitch.nowDark' : 'themeSwitch.nowLight') : t(option.label);
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={choice === option.id}
            aria-label={name}
            tabIndex={choice === option.id ? 0 : -1}
            className={styles.themeOption}
            onClick={() => {
              set(option.id);
            }}
            onKeyDown={(event) => {
              onKeyDown(event, index);
            }}
            onPointerEnter={(event) => {
              if (event.pointerType === 'mouse') setPeek(index);
            }}
            onFocus={(event) => {
              if (event.currentTarget.matches(':focus-visible')) setPeek(index);
            }}
            onBlur={() => {
              setPeek(null);
            }}
            data-theme-option={option.id}
            data-testid={`theme-${option.id}`}
          >
            {option.icon}
          </button>
        );
      })}
      {peeked ? (
        <span className={styles.themePeek} style={{ '--peek-at': `${String(optionCentre(peek ?? 0))}px` } as CSSProperties} aria-hidden="true" data-testid="theme-peek">
          <ThemePreview kind={peeked.id} />
          <span className={styles.themePeekLabel}>{caption}</span>
        </span>
      ) : null}
    </div>
  );
}
